import type { ReactNode } from "react";

/**
 * Props for {@link EvCoverageStatusLayout}.
 *
 * @property planSummary - Plan summary card rendered in the fixed top area.
 * @property children - Status content (typically an `InactiveInsurance` variant).
 * @property spacing - `"padded"` (default) for EV status states, `"flush"` for the
 *   inactive-plan fallback which uses tighter vertical spacing.
 */
export interface EvCoverageStatusLayoutProps {
  planSummary: ReactNode;
  children: ReactNode;
  spacing?: "padded" | "flush";
}

/**
 * Shared column layout for EV coverage status states: a fixed plan-summary card on
 * top and a scrollable status region below. Deduplicates the near-identical wrappers
 * for the error, in-progress, not-supported, and inactive-fallback branches.
 *
 * @param props - {@link EvCoverageStatusLayoutProps}
 * @returns The two-row status layout.
 */
export function EvCoverageStatusLayout({
  planSummary,
  children,
  spacing = "padded",
}: EvCoverageStatusLayoutProps) {
  const outerClassName =
    spacing === "padded"
      ? "flex min-h-0 flex-1 flex-col gap-2 overflow-hidden pt-2 md:gap-3 md:pt-4"
      : "flex min-h-0 flex-1 flex-col gap-2 md:gap-3";
  const contentClassName =
    spacing === "padded"
      ? "flex min-h-0 flex-1 flex-col overflow-auto py-2 md:py-4"
      : "flex min-h-0 flex-1 flex-col overflow-auto";

  return (
    <div className={outerClassName}>
      <div className="shrink-0">{planSummary}</div>
      <div className={contentClassName}>{children}</div>
    </div>
  );
}

export default EvCoverageStatusLayout;
